/** Work order detail: header, transitions, tasks/checklist, labour, parts, history. */
import { Link, useParams } from "@tanstack/react-router";
import { ChevronDown, Pencil, Plus } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, errorMessage } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { AssetReference } from "@/features/entities/AssetReference";
import { PersonReference } from "@/features/entities/PersonReference";
import { DocumentFileLink } from "@/features/entities/DocumentFileLink";
import { checklistAnswer } from "@/features/workorders/checklist";
import { Attachments } from "@/features/documents/Attachments";
import {
  FailureReportForm,
  MeterReadingForm,
  PartsPanel,
} from "@/features/workorders/ExecutionPanels";
import { WorkOrderAssignees } from "@/features/workorders/WorkOrderAssignees";
import { WorkOrderDueDate } from "@/features/workorders/WorkOrderDueDate";
import { WorkOrderPlanning, type PlanningAction } from "@/features/workorders/WorkOrderPlanning";
import { SupervisorReview } from "@/features/workorders/SupervisorReview";
import { useFollowUpRepair } from "@/features/requests/queries";
import {
  canPlanTasks,
  isTaskUntouched,
  TaskDefinitionDialog,
} from "@/features/workorders/TaskDefinitionDialog";
import {
  type ChecklistItem,
  type Task,
  type TransitionOption,
  type WorkOrderDetail,
  useAddLabour,
  useAnswerChecklist,
  useTransition,
  useUpdateTask,
  useWorkOrder,
  useWorkOrderAssigneeOptions,
} from "@/features/workorders/queries";
import {
  Details,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "@/shared/components/page";
import { cn, formatDateTime, formatMoney, formatQuantity } from "@/shared/lib/format";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select, Textarea } from "@/shared/ui/form";
import {
  Badge,
  Dialog,
  Table,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Td,
  Th,
  Tr,
} from "@/shared/ui/primitives";

function toLocalDateTimeValue(date: Date): string {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function defaultScheduleRange(): { start: string; end: string } {
  const today = new Date();
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  const end = new Date(today);
  end.setHours(23, 59, 0, 0);
  const toLocalValue = (date: Date): string =>
    new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  return { start: toLocalValue(start), end: toLocalValue(end) };
}

function defaultStartDate(): string {
  const now = new Date();
  now.setSeconds(0, 0);
  return toLocalDateTimeValue(now);
}

/** Dialog collecting transition inputs (reason, completion data). */
export function TransitionDialog({
  woId,
  option,
  assignees,
  completionMeterRequired,
  completionMeterUnit,
  completionMeterValue,
  onClose,
}: {
  woId: string;
  option: TransitionOption;
  assignees: WorkOrderDetail["assignees"];
  completionMeterRequired: boolean;
  completionMeterUnit: string | null;
  completionMeterValue: string | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const transition = useTransition(woId);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [meter, setMeter] = useState(completionMeterValue ?? "");
  const [testResult, setTestResult] = useState("");
  const [testNotes, setTestNotes] = useState("");
  const [returnToService, setReturnToService] = useState(true);
  const [dirty, setDirty] = useState(false);
  const requestClose = (): void => {
    if (!dirty || window.confirm(t("workOrders.discardChanges"))) onClose();
  };
  const [scheduleRange, setScheduleRange] = useState(defaultScheduleRange);
  const [actualStart, setActualStart] = useState(defaultStartDate);
  const defaultAssignee = assignees.find((assignee) => assignee.role === "lead") ?? assignees[0];
  const [assigneeId, setAssigneeId] = useState(defaultAssignee?.membership_id ?? "");
  const isSchedule = option.name === "schedule";
  const isStart = option.name === "start";
  const isComplete = option.name === "complete";
  const isAcceptance = option.name === "verify" || option.name === "release";
  const requiresMeterNow =
    completionMeterRequired && (isComplete || (isAcceptance && completionMeterValue === null));
  const invalidCompletionMeter = requiresMeterNow && !meter.trim();
  const canChooseAssignee = can("workorders.workorder.plan") && can("identity.user.view");
  const assigneeOptions = useWorkOrderAssigneeOptions(isStart && canChooseAssignee);
  const nowInput = toLocalDateTimeValue(new Date());
  const invalidStart =
    isStart &&
    (!assigneeId ||
      !actualStart ||
      new Date(actualStart).getTime() > Date.now() ||
      Number.isNaN(new Date(actualStart).getTime()));
  const invalidScheduleRange =
    isSchedule &&
    (!scheduleRange.start || !scheduleRange.end || scheduleRange.end <= scheduleRange.start);
  const needsReason = option.requires_reason && !reason.trim();
  const label = t(`workOrders.transition.${option.name}`, { defaultValue: option.label });

  const submit = (e: { preventDefault(): void }): void => {
    e.preventDefault();
    transition.mutate(
      {
        name: option.name,
        body: {
          reason: reason || null,
          scheduled_start:
            isSchedule && scheduleRange.start ? new Date(scheduleRange.start).toISOString() : null,
          scheduled_end:
            isSchedule && scheduleRange.end ? new Date(scheduleRange.end).toISOString() : null,
          assignee_id: isStart && assigneeId ? assigneeId : null,
          actual_start: isStart && actualStart ? new Date(actualStart).toISOString() : null,
          completion_notes: isComplete ? notes || null : null,
          meter_value: (isComplete || isAcceptance) && meter ? meter : null,
          return_to_service: returnToService,
          auto_close: false,
          functional_test_result: testResult || null,
          functional_test_notes: testNotes || null,
        },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && requestClose()}
      title={t("workOrders.transitionTitle", { label })}
    >
      <form onSubmit={submit} onChange={() => setDirty(true)} className="space-y-4">
        {!isStart ? (
          <Field
            id="tr-reason"
            label={`${t("app.reason")}${option.requires_reason ? "" : ` (${t("app.optional")})`}`}
            error={needsReason && transition.isError ? t("app.reasonRequired") : undefined}
          >
            <Textarea id="tr-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        ) : null}
        {isComplete ? (
          <>
            <Field id="tr-test-result" label={t("workOrders.functionalTest")}>
              <Select
                id="tr-test-result"
                value={testResult}
                onChange={(event) => setTestResult(event.target.value)}
              >
                <option value="">—</option>
                <option value="pass">{t("workOrders.checkPass")}</option>
                <option value="fail">{t("workOrders.checkFail")}</option>
              </Select>
            </Field>
            <Field id="tr-test-notes" label={t("workOrders.testEvidence")}>
              <Textarea
                id="tr-test-notes"
                value={testNotes}
                onChange={(event) => setTestNotes(event.target.value)}
              />
            </Field>
          </>
        ) : null}
        {option.name === "verify" ? (
          <Checkbox
            id="verify-release"
            label={t("workOrders.returnToService")}
            checked={returnToService}
            onChange={(event) => setReturnToService(event.target.checked)}
          />
        ) : null}
        {isSchedule ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field id="tr-scheduled-start" label={t("workOrders.scheduledStart")}>
              <Input
                id="tr-scheduled-start"
                type="datetime-local"
                required
                value={scheduleRange.start}
                onChange={(event) =>
                  setScheduleRange((current) => ({ ...current, start: event.target.value }))
                }
              />
            </Field>
            <Field id="tr-scheduled-end" label={t("workOrders.scheduledEnd")}>
              <Input
                id="tr-scheduled-end"
                type="datetime-local"
                required
                min={scheduleRange.start}
                value={scheduleRange.end}
                onChange={(event) =>
                  setScheduleRange((current) => ({ ...current, end: event.target.value }))
                }
              />
            </Field>
          </div>
        ) : null}
        {isStart ? (
          <>
            <Field id="tr-assignee" label={t("workOrders.assignee")}>
              <Select
                id="tr-assignee"
                required
                disabled={!canChooseAssignee}
                value={assigneeId}
                onChange={(event) => setAssigneeId(event.target.value)}
              >
                <option value="">{t("workOrders.selectAssignee")}</option>
                {assignees
                  .filter(
                    (assignee) =>
                      !assigneeOptions.items.some((member) => member.id === assignee.membership_id),
                  )
                  .map((assignee) => (
                    <option key={assignee.membership_id} value={assignee.membership_id}>
                      {assignee.display_name}
                    </option>
                  ))}
                {assigneeOptions.items.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.user.display_name || member.user.email}
                  </option>
                ))}
              </Select>
            </Field>
            {canChooseAssignee && assigneeOptions.hasMore ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={assigneeOptions.isLoadingMore}
                onClick={assigneeOptions.loadMore}
              >
                {t("workOrders.loadMoreAssignees")}
              </Button>
            ) : null}
            {canChooseAssignee && assigneeOptions.query.isPending ? (
              <p className="text-sm text-muted-foreground">{t("app.loading")}</p>
            ) : null}
            {assigneeOptions.query.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {errorMessage(assigneeOptions.query.error)}
              </p>
            ) : null}
            {!canChooseAssignee && !assigneeId ? (
              <p role="alert" className="text-sm text-destructive">
                {t("workOrders.startAssignmentPermissionRequired")}
              </p>
            ) : null}
            <Field id="tr-actual-start" label={t("workOrders.actualStart")}>
              <Input
                id="tr-actual-start"
                type="datetime-local"
                required
                max={nowInput}
                value={actualStart}
                onChange={(event) => setActualStart(event.target.value)}
              />
            </Field>
            {invalidStart && actualStart && new Date(actualStart).getTime() > Date.now() ? (
              <p role="alert" className="text-sm text-destructive">
                {t("workOrders.startDateCannotBeFuture")}
              </p>
            ) : null}
          </>
        ) : null}
        {isComplete ||
        (isAcceptance && completionMeterRequired && completionMeterValue === null) ? (
          <>
            {isComplete ? (
              <Field id="tr-notes" label={t("workOrders.completionNotes")}>
                <Textarea id="tr-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>
            ) : null}
            <Field
              id="tr-meter"
              label={
                completionMeterRequired
                  ? t("feedbackMaintenance.completionMeterLabel", {
                      unit: completionMeterUnit ?? "",
                    })
                  : t("workOrders.meterValue")
              }
              hint={
                completionMeterRequired ? t("feedbackMaintenance.completionMeterHint") : undefined
              }
            >
              <Input
                id="tr-meter"
                type="number"
                min="0"
                step="any"
                required={requiresMeterNow}
                value={meter}
                onChange={(e) => setMeter(e.target.value)}
              />
            </Field>
            {isComplete ? (
              <Checkbox
                id="tr-rts"
                label={t("workOrders.returnToService")}
                checked={returnToService}
                onChange={(e) => setReturnToService(e.target.checked)}
              />
            ) : null}
          </>
        ) : null}
        {invalidCompletionMeter ? (
          <p role="alert" className="text-sm text-destructive">
            {t("feedbackMaintenance.completionMeterRequired")}
          </p>
        ) : null}
        {invalidScheduleRange ? (
          <p className="text-sm text-destructive" role="alert">
            {t("workOrders.invalidScheduleRange")}
          </p>
        ) : null}
        {transition.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {transition.error instanceof ApiError && transition.error.code === "reason_required"
              ? t("app.reasonRequired")
              : errorMessage(transition.error)}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={requestClose}>
            {t("app.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={
              transition.isPending ||
              needsReason ||
              invalidScheduleRange ||
              invalidStart ||
              invalidCompletionMeter
            }
          >
            {label}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Checklist item editor: renders an input matching the response type. */
export function ChecklistRow({
  woId,
  item,
  disabled,
}: {
  woId: string;
  item: ChecklistItem;
  disabled: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const answer = useAnswerChecklist(woId);
  const evidence = checklistAnswer(item.value);
  const [value, setValue] = useState(evidence.value);
  const [photoId, setPhotoId] = useState(evidence.photoDocumentId);
  useEffect(() => {
    setValue(evidence.value);
    setPhotoId(evidence.photoDocumentId);
  }, [evidence.value, evidence.photoDocumentId]);
  const save = (v: typeof value): void => {
    setValue(v);
    if (item.photo_required && !photoId) return;
    answer.mutate({
      itemId: item.id,
      value: v,
      note: item.note,
      photoDocumentId: photoId,
    });
  };
  const selectPhoto = (documentId: string): void => {
    setPhotoId(documentId);
    if (value === null && item.response_type !== "photo") return;
    answer.mutate({
      itemId: item.id,
      value,
      note: item.note,
      photoDocumentId: documentId,
    });
  };

  let control: ReactElement;
  switch (item.response_type) {
    case "choice": {
      const options = item.constraints.choices ?? item.constraints.options;
      control = (
        <Select
          aria-label={item.prompt}
          disabled={disabled}
          value={String(value ?? "")}
          onChange={(event) => save(event.target.value || null)}
        >
          <option value="">—</option>
          {Array.isArray(options)
            ? options
                .filter((option): option is string => typeof option === "string")
                .map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))
            : null}
        </Select>
      );
      break;
    }
    case "yes_no":
    case "boolean":
    case "pass_fail":
      control = (
        <Select
          aria-label={item.prompt}
          disabled={disabled}
          value={value === null || value === undefined ? "" : String(value)}
          onChange={(e) =>
            save(
              e.target.value === ""
                ? null
                : item.response_type === "pass_fail"
                  ? e.target.value
                  : e.target.value === "true",
            )
          }
        >
          <option value="">—</option>
          <option value={item.response_type === "pass_fail" ? "pass" : "true"}>
            {item.response_type === "pass_fail" ? t("workOrders.checkPass") : t("app.yes")}
          </option>
          <option value={item.response_type === "pass_fail" ? "fail" : "false"}>
            {item.response_type === "pass_fail" ? t("workOrders.checkFail") : t("app.no")}
          </option>
        </Select>
      );
      break;
    case "number":
    case "reading":
      control = (
        <Input
          aria-label={item.prompt}
          type="number"
          step="any"
          disabled={disabled}
          value={value === null ? "" : String(value)}
          onChange={(event) =>
            setValue(event.target.value === "" ? null : Number(event.target.value))
          }
          onBlur={(e) => e.target.value !== "" && save(Number(e.target.value))}
        />
      );
      break;
    default:
      control = (
        <Input
          aria-label={item.prompt}
          disabled={disabled}
          value={value === null ? "" : String(value)}
          onChange={(event) => setValue(event.target.value)}
          onBlur={(e) => e.target.value !== String(evidence.value ?? "") && save(e.target.value)}
        />
      );
  }
  return (
    <li className="grid grid-cols-1 items-center gap-2 py-1.5 sm:grid-cols-2">
      <span className="text-sm">
        {`${item.sequence}. ${item.prompt}`}
        {item.is_mandatory ? <span className="text-destructive"> *</span> : null}
        {item.photo_required ? (
          <span className="ms-1 text-xs text-muted-foreground">({t("jobTemplates.photo")})</span>
        ) : null}
      </span>
      <div className="space-y-1">
        {control}
        {photoId ? (
          <DocumentFileLink
            id={photoId}
            versionId={photoId === evidence.photoDocumentId ? evidence.photoVersionId : undefined}
            label={t("fileEvidence.openEvidence")}
          />
        ) : null}
        {item.photo_required &&
        (!photoId || (value === null && item.response_type !== "photo")) &&
        !disabled ? (
          <p role="status" className="text-sm text-muted-foreground">
            {t("fileEvidence.answerAndPhoto")}
          </p>
        ) : null}
        {answer.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(answer.error)}
          </p>
        ) : null}
        {item.photo_required && !disabled ? (
          <Attachments
            entityType="workorder"
            entityId={woId}
            compact
            accept="image/*"
            fileKind="photo"
            relation="evidence"
            allowAlreadyLinked
            onSelected={selectPhoto}
          />
        ) : null}
      </div>
    </li>
  );
}

/** A task card with its checklist and done/skip actions. */
export function TaskCard({
  woId,
  task,
  editable,
  onEdit,
}: {
  woId: string;
  task: Task;
  editable: boolean;
  onEdit?: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const update = useUpdateTask(woId);
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const open = task.status === "pending";
  return (
    <div className="rounded-md border p-3">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row">
        <div className="min-w-0 [overflow-wrap:anywhere]">
          <div className="font-medium">
            {`${task.sequence}. ${task.title}`}
            {task.is_mandatory ? <span className="text-destructive"> *</span> : null}
          </div>
          {task.instructions ? (
            <p className="text-sm text-muted-foreground">{task.instructions}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {onEdit ? (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("workOrders.editTask")}
              title={t("workOrders.editTask")}
              onClick={onEdit}
            >
              <Pencil className="size-3.5" aria-hidden="true" />
            </Button>
          ) : null}
          <StatusBadge value={task.status} namespace="workOrders.taskStatus" />
          {editable && open ? (
            <>
              <Button
                size="sm"
                onClick={() => update.mutate({ taskId: task.id, status: "done" })}
                disabled={update.isPending}
              >
                {t("workOrders.taskDone")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSkipOpen(true)}
                disabled={update.isPending}
              >
                {t("workOrders.taskSkipped")}
              </Button>
            </>
          ) : null}
        </div>
        {skipOpen ? (
          <div className="mt-3 space-y-2">
            <Field id={`skip-${task.id}`} label={t("workOrders.skipReason")}>
              <Textarea
                id={`skip-${task.id}`}
                value={skipReason}
                onChange={(event) => setSkipReason(event.target.value)}
              />
            </Field>
            <Button
              size="sm"
              disabled={update.isPending || !skipReason.trim()}
              onClick={() =>
                update.mutate(
                  { taskId: task.id, status: "skipped", notes: skipReason },
                  { onSuccess: () => setSkipOpen(false) },
                )
              }
            >
              {t("workOrders.taskSkipped")}
            </Button>
          </div>
        ) : null}
        {task.notes ? <p className="mt-2 text-sm">{task.notes}</p> : null}
        {task.performer_name ? (
          <p className="text-xs text-muted-foreground">{task.performer_name}</p>
        ) : null}
      </div>
      {task.checklist.length > 0 ? (
        <ul className="mt-2 divide-y border-t pt-1">
          {task.checklist.map((item) => (
            <ChecklistRow key={item.id} woId={woId} item={item} disabled={!editable || !open} />
          ))}
        </ul>
      ) : null}
      {update.isError ? (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {errorMessage(update.error)}
        </p>
      ) : null}
    </div>
  );
}

/** Inline labour entry form. */
export function LabourForm({ woId }: { woId: string }): ReactElement {
  const { t } = useTranslation();
  const add = useAddLabour(woId);
  const [hours, setHours] = useState("");
  const [activity, setActivity] = useState("");
  const submit = (e: { preventDefault(): void }): void => {
    e.preventDefault();
    add.mutate(
      { hours: Number(hours), activity },
      {
        onSuccess: () => {
          setHours("");
          setActivity("");
        },
      },
    );
  };
  return (
    <form
      onSubmit={submit}
      className="grid grid-cols-1 items-end gap-3 rounded-md border p-3 sm:flex sm:flex-wrap"
    >
      <Field id="labour-hours" label={t("workOrders.hours")}>
        <Input
          id="labour-hours"
          type="number"
          step="0.25"
          min="0.25"
          className="w-full sm:w-28"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          required
        />
      </Field>
      <Field id="labour-activity" label={t("workOrders.activity")}>
        <Input
          id="labour-activity"
          className="w-full sm:w-64"
          value={activity}
          onChange={(e) => setActivity(e.target.value)}
        />
      </Field>
      <Button type="submit" disabled={add.isPending || !hours}>
        {t("workOrders.addLabour")}
      </Button>
      {add.isError ? (
        <p role="alert" className="w-full text-sm text-destructive">
          {errorMessage(add.error)}
        </p>
      ) : null}
    </form>
  );
}

/** Header KPIs/details for a work order. */
function Summary({ wo }: { wo: WorkOrderDetail }): ReactElement {
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  return (
    <Details
      items={[
        {
          label: t("app.asset"),
          value: (
            <AssetReference
              id={wo.asset.id}
              code={wo.asset.code}
              name={wo.asset.name}
              context="work-order"
            />
          ),
        },
        { label: t("app.site"), value: wo.site?.name ?? "—" },
        {
          label: t("workOrders.accepted"),
          value: `${formatDateTime(wo.accepted_at)} · ${wo.accepted_by_name ?? t("workOrders.system")}`,
        },
        { label: t("workOrders.released"), value: formatDateTime(wo.released_at) },
        { label: t("workOrders.actualStart"), value: formatDateTime(wo.actual_start) },
        { label: t("workOrders.actualEnd"), value: formatDateTime(wo.actual_end) },
        {
          label: t("workOrders.requiresVerification"),
          value: wo.requires_verification ? t("app.yes") : t("app.no"),
        },
        ...(can("workorders.cost.view")
          ? [
              {
                label: `${t("workOrders.costs")} · ${t("workOrders.estimated")}`,
                value: formatMoney(wo.est_cost, i18n.language),
              },
              {
                label: `${t("workOrders.costs")} · ${t("workOrders.actual")}`,
                value: formatMoney(wo.actual_cost, i18n.language),
              },
            ]
          : []),
        { label: t("app.description"), value: wo.description || "—" },
        ...(wo.completion_notes
          ? [{ label: t("workOrders.completionNotes"), value: wo.completion_notes }]
          : []),
      ]}
    />
  );
}

/** Work order detail page. */
export function WorkOrderDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { woId = "" } = useParams({ strict: false });
  const { can } = useAuth();
  const q = useWorkOrder(woId);
  const [active, setActive] = useState<TransitionOption | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [taskEditor, setTaskEditor] = useState<Task | "new" | null>(null);
  const [planning, setPlanning] = useState<PlanningAction | null>(null);
  const followUp = useFollowUpRepair(q.data?.service_request_id ?? "", woId);

  if (q.isPending) {
    return <LoadingState />;
  }
  if (q.isError) {
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  }
  const wo = q.data;
  const editable =
    wo.status === "in_progress" &&
    (can("workorders.checklist.fill") || can("workorders.workorder.update_assigned"));
  const allowed = wo.transitions.filter((o) => o.allowed);
  const planTasks = can("workorders.workorder.plan") && canPlanTasks(wo.status);
  const canEditPlanning =
    ["draft", "planned", "scheduled"].includes(wo.status) &&
    (can("workorders.workorder.plan") || can("workorders.workorder.edit"));

  return (
    <div>
      <PageHeader
        title={`${wo.code} · ${wo.title}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge value={wo.status} namespace="workOrders.status" />
            <StatusBadge value={wo.priority} namespace="workOrders.priority" />
            <Badge tone="outline">{t(`workOrders.type.${wo.type}`)}</Badge>
            {wo.is_overdue ? <Badge tone="danger">{t("app.overdue")}</Badge> : null}
            {wo.hold_reason ? <span className="text-xs">({wo.hold_reason})</span> : null}
          </span>
        }
        actions={allowed.map((o) => (
          <Button
            key={o.name}
            variant={
              o.name === "cancel" || o.name === "reject"
                ? "destructive"
                : o.name === "complete" || o.name === "start"
                  ? "default"
                  : "outline"
            }
            onClick={() =>
              o.name === "plan" || o.name === "schedule" ? setPlanning(o.name) : setActive(o)
            }
          >
            {t(`workOrders.transition.${o.name}`, { defaultValue: o.label })}
          </Button>
        ))}
      />
      <div className="mb-3 flex flex-wrap gap-3">
        {wo.service_request_id ? (
          <Link to="/requests/$requestId" params={{ requestId: wo.service_request_id }}>
            {t("workOrders.sourceFault")}
          </Link>
        ) : null}
        {wo.parent_id ? (
          <Link to="/work-orders/$woId" params={{ woId: wo.parent_id }}>
            {t("workOrders.parentInspection")}
          </Link>
        ) : null}
        {wo.vessel_id ? (
          <AssetReference id={wo.vessel_id} name={t("workOrders.vessel")} context="history" />
        ) : null}
        {wo.type === "inspection" &&
        wo.service_request_id &&
        can("requests.request.triage") &&
        can("workorders.workorder.create") ? (
          <Button variant="outline" disabled={followUp.isPending} onClick={() => followUp.mutate()}>
            {t("workOrders.followUpRepair")}
          </Button>
        ) : null}
      </div>
      {followUp.isError ? <p role="alert">{errorMessage(followUp.error)}</p> : null}
      {followUp.data ? (
        <Link to="/work-orders/$woId" params={{ woId: followUp.data.id }}>
          {followUp.data.code}
        </Link>
      ) : null}
      <section className="mb-4 rounded-lg border p-4" aria-labelledby="work-order-planning">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 id="work-order-planning" className="text-lg font-semibold">
            {t("workOrders.planningAllocation")}
          </h2>
          {canEditPlanning ? (
            <Button variant="outline" onClick={() => setPlanning("edit")}>
              {t("workOrders.editPlanning")}
            </Button>
          ) : null}
        </div>
        <dl className="grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt className="text-sm text-muted-foreground">{t("workOrders.team")}</dt>
            <dd>{wo.assigned_team?.name ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">{t("workOrders.vendor")}</dt>
            <dd>{wo.vendor?.name ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">{t("workOrders.operatingUnit")}</dt>
            <dd>{wo.operating_unit?.name ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">{t("workOrders.costCenter")}</dt>
            <dd>{wo.cost_center?.name ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">{t("workOrders.estHours")}</dt>
            <dd>{wo.est_hours ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">{t("app.due")}</dt>
            <dd>
              <WorkOrderDueDate
                workOrderId={wo.id}
                status={wo.status}
                dueAt={wo.due_at}
                isOverdue={wo.is_overdue}
              />
            </dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">{t("workOrders.scheduledStart")}</dt>
            <dd>{formatDateTime(wo.scheduled_start)}</dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">{t("workOrders.scheduledEnd")}</dt>
            <dd>{formatDateTime(wo.scheduled_end)}</dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">{t("workOrders.assignees")}</dt>
            <dd>
              <WorkOrderAssignees workOrderId={wo.id} status={wo.status} assignees={wo.assignees} />
            </dd>
          </div>
        </dl>
      </section>
      {planning ? (
        <WorkOrderPlanning wo={wo} action={planning} onClose={() => setPlanning(null)} />
      ) : null}
      {active ? (
        <TransitionDialog
          woId={wo.id}
          option={active}
          assignees={wo.assignees}
          completionMeterRequired={wo.completion_meter_required ?? false}
          completionMeterUnit={wo.completion_meter_unit ?? null}
          completionMeterValue={wo.meter_at_completion?.toString() ?? null}
          onClose={() => setActive(null)}
        />
      ) : null}
      {taskEditor && planTasks ? (
        <TaskDefinitionDialog
          woId={wo.id}
          task={taskEditor === "new" ? undefined : taskEditor}
          onClose={() => setTaskEditor(null)}
        />
      ) : null}
      <Button
        variant="outline"
        className="mb-3 w-full justify-between sm:hidden"
        aria-expanded={summaryOpen}
        aria-controls="work-order-summary"
        onClick={() => setSummaryOpen(!summaryOpen)}
      >
        {t("workOrders.details")}
        <ChevronDown className={cn("size-4 shrink-0", summaryOpen && "rotate-180")} aria-hidden />
      </Button>
      <div id="work-order-summary" className={cn(!summaryOpen && "hidden", "sm:block")}>
        <Summary wo={wo} />
      </div>

      <Tabs defaultValue="tasks" className="mt-6">
        <TabsList>
          <TabsTrigger value="tasks">
            {t("workOrders.tasks")} ({wo.tasks.length})
          </TabsTrigger>
          <TabsTrigger value="labour">
            {t("workOrders.labour")} ({wo.labour.length})
          </TabsTrigger>
          <TabsTrigger value="parts">
            {t("workOrders.parts")} ({wo.parts.length})
          </TabsTrigger>
          <TabsTrigger value="documents">{t("documents.title")}</TabsTrigger>
          <TabsTrigger value="history">{t("workOrders.history")}</TabsTrigger>
          <TabsTrigger value="review">{t("workOrders.supervisorReview")}</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="space-y-3">
          {planTasks ? (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setTaskEditor("new")}>
                <Plus className="size-4" aria-hidden="true" />
                {t("workOrders.addTask")}
              </Button>
            </div>
          ) : null}
          {wo.status === "in_progress" ? <MeterReadingForm wo={wo} /> : null}
          {wo.tasks.length === 0 ? (
            <EmptyState message={t("workOrders.noTasks")} />
          ) : (
            wo.tasks.map((task) => (
              <TaskCard
                key={task.id}
                woId={wo.id}
                task={task}
                editable={editable}
                onEdit={planTasks && isTaskUntouched(task) ? () => setTaskEditor(task) : undefined}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="labour" className="space-y-3">
          {wo.status === "in_progress" && can("workorders.labour.record") ? (
            <LabourForm woId={wo.id} />
          ) : null}
          {wo.labour.length === 0 ? (
            <EmptyState />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t("workOrders.activity")}</Th>
                  <Th>{t("workOrders.performer")}</Th>
                  <Th>{t("workOrders.hours")}</Th>
                  <Th>{t("app.created")}</Th>
                  {can("workorders.cost.view") ? <Th>{t("workOrders.costs")}</Th> : null}
                </tr>
              </thead>
              <tbody>
                {wo.labour.map((l) => (
                  <Tr key={l.id}>
                    <Td>{l.activity || "—"}</Td>
                    <Td>
                      <PersonReference
                        id={l.membership_id}
                        name={l.performer_name ?? t("workOrders.system")}
                      />
                      {l.uncosted ? <Badge tone="outline">{t("workOrders.uncosted")}</Badge> : null}
                    </Td>
                    <Td className="tabular-nums">{formatQuantity(l.hours)}</Td>
                    <Td>{formatDateTime(l.started_at)}</Td>
                    {can("workorders.cost.view") ? (
                      <Td className="tabular-nums">
                        {l.uncosted ? t("workOrders.uncosted") : formatQuantity(l.cost)}
                      </Td>
                    ) : null}
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </TabsContent>

        <TabsContent value="parts">
          <PartsPanel wo={wo} />
        </TabsContent>

        <TabsContent value="documents">
          <Attachments entityType="workorder" entityId={wo.id} />
        </TabsContent>
        <TabsContent value="review" className="space-y-4">
          <SupervisorReview wo={wo} />
        </TabsContent>

        <TabsContent value="history">
          <Table>
            <thead>
              <tr>
                <Th>{t("app.updated")}</Th>
                <Th>{t("app.status")}</Th>
                <Th>{t("app.reason")}</Th>
                <Th>{t("workOrders.performer")}</Th>
              </tr>
            </thead>
            <tbody>
              {wo.status_history.map((h, i) => (
                <Tr key={`${h.occurred_at}-${i}`}>
                  <Td>{formatDateTime(h.occurred_at)}</Td>
                  <Td className="flex items-center gap-1">
                    <StatusBadge value={h.from_status} namespace="workOrders.status" />→
                    <StatusBadge value={h.to_status} namespace="workOrders.status" />
                  </Td>
                  <Td>{h.reason || "—"}</Td>
                  <Td>
                    <PersonReference
                      id={h.actor_id}
                      name={h.actor_name ?? t("workOrders.system")}
                    />
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          {wo.downtime.length > 0 ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Downtime:{" "}
              {wo.downtime
                .map((d) => `${formatDateTime(d.started_at)} → ${formatDateTime(d.ended_at)}`)
                .join("; ")}
            </p>
          ) : null}
          {wo.type === "corrective" || wo.failure_report ? (
            <div className="mt-3">
              <FailureReportForm
                wo={wo}
                editable={
                  editable || (wo.status === "completed" && can("workorders.workorder.verify"))
                }
              />
            </div>
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}
